import React from 'react';
import ReactDOM from 'react-dom/client';
import { ConfigProvider } from 'antd';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import App from './App';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useMeQuery } from './queries/me';
import { usePhone } from './hooks/usePhone';
import { phoneControls } from './theme/tokens';
import { resolvePrimary, onColor, brandInk } from './utils/branding';
import { queryClient } from './queries/queryClient';
import './index.css';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

window.addEventListener('beforeinstallprompt', (event) => {
  event.preventDefault();
  window.deferredInstallPrompt = event;
  window.dispatchEvent(new Event('pwa-install-available'));
});

const fontFamily =
  "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

// AntD colorPrimary is read by JS (not a CSS var), so it has to react to the
// org's brand color. Defaults to our navy until the org loads / when logged out.
function BrandedConfigProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const { data } = useMeQuery({ enabled: isAuthenticated });
  const primary = resolvePrimary(data?.organization?.branding?.primaryColor);
  const phone = usePhone();
  const theme = {
    token: {
      colorPrimary: primary,
      // Text ON solid primary fills (primary buttons) — contrast-safe.
      colorTextLightSolid: onColor(primary),
      // Brand-as-text (links, text buttons) kept dark enough to read on white.
      colorLink: brandInk(primary),
      borderRadius: 8,
      fontFamily,
      ...(phone && phoneControls)
    }
  };
  return <ConfigProvider theme={theme}>{children}</ConfigProvider>;
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <BrandedConfigProvider>
            <App />
          </BrandedConfigProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" mobileOffset={{ top: 72 }} richColors closeButton />
    </QueryClientProvider>
  </React.StrictMode>
);

serviceWorkerRegistration.register();
